import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  BatteryCharging,
  BookOpen,
  BrainCircuit,
  Check,
  ChevronRight,
  CircleStop,
  Clock3,
  Database,
  Gauge,
  HardDrive,
  Home,
  Info,
  Layers3,
  MemoryStick,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Trash2,
  Trophy,
  WifiOff,
  X,
  Zap,
} from 'lucide-react'
import { PokerTable, type PokerTablePlayer } from './components/PokerTable'
import { RangeGrid } from './components/RangeGrid'
import { applyPreset, createBenchmarkConfig, presetMeta } from './data/presets'
import {
  db,
  deleteSolverResult,
  getTrainerProgress,
  listSolverResults,
  recordTrainerAnswer,
  recordTrainerSessionComplete,
  saveSolverResult,
} from './db'
import { runBrowserBenchmark } from './lib/benchmark'
import { batchMixLabel, createBatchPlan, type BatchStreetMix } from './lib/batch'
import { displayCard, isRedCard, RANKS_DESC, SUITS, validateBoard } from './lib/cards'
import { explainCombo } from './lib/explanation'
import { compactRangeSummary, parseRange } from './lib/range'
import {
  buildTrainerQuestions,
  createTrainerDeck,
  evaluatePreflopAnswer,
  evaluateTrainerAnswer,
  trainerPoolStats,
  resultTrainingProfile,
  trainerActionLabel,
  trainerQuestionCategory,
  type TrainerEvaluation,
  type TrainerQuestion,
  type TrainerSourceMode,
} from './lib/trainer'
import { createRecalculationConfig, getBetSizes, setBetSizes, strategicChoiceLabel, type BatchActionSet } from './lib/tree'
import { solverClient } from './solver/client'
import { useAppStore, type ResultTab, type Screen } from './store/useAppStore'
import type { Card, DeviceBenchmark, SolverError, SolverPresetId, SolverProgress, SolverResult, StrategyNode, Street, TrainerProgress, TreeConfig } from './types/solver'

const MB = 1024 * 1024
const phaseLabels = {
  'building-tree': 'ゲームツリーを構築中',
  'allocating-memory': 'メモリを確保中',
  solving: '戦略を反復計算中',
  compressing: '戦略データを圧縮中',
  finalizing: '結果を確定中',
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '—'
  return bytes >= 1024 * MB ? `${(bytes / (1024 * MB)).toFixed(2)}GB` : `${(bytes / MB).toFixed(1)}MB`
}

function formatTime(ms?: number): string {
  if (ms === undefined) return '—'
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}秒`
  return `${Math.floor(ms / 60_000)}分${Math.round((ms % 60_000) / 1000)}秒`
}

function CardChip({ card, small = false }: { card: Card; small?: boolean }) {
  return <span className={`playing-card ${small ? 'small' : ''} ${isRedCard(card) ? 'red' : ''}`}>{displayCard(card)}</span>
}

function AppHeader({ title, back }: { title: string; back?: () => void }) {
  return (
    <header className="app-header">
      <div className="header-left">
        {back ? <button className="icon-button" onClick={back} aria-label="戻る"><ArrowLeft size={20} /></button> : <div className="brand-mark">G</div>}
        <div>
          <strong>{title}</strong>
          <span>ON-DEVICE · v0.4</span>
        </div>
      </div>
      <div className="status-pill"><span /> Local WASM</div>
    </header>
  )
}

function BottomNav({ screen, go }: { screen: Screen; go: (screen: Screen) => void }) {
  if (screen === 'solving' || screen === 'batch') return null
  return (
    <nav className="bottom-nav">
      <button className={screen === 'home' ? 'active' : ''} onClick={() => go('home')}><Home size={19} /><span>ホーム</span></button>
      <button className={screen === 'setup' ? 'active center' : 'center'} onClick={() => go('setup')}><Plus size={22} /><span>新規計算</span></button>
      <button className={screen === 'trainer' ? 'active' : ''} onClick={() => go('trainer')}><Target size={19} /><span>トレーナー</span></button>
      <button className={screen === 'saved' ? 'active' : ''} onClick={() => go('saved')}><Database size={19} /><span>保存結果</span></button>
    </nav>
  )
}

function Notice({ type = 'info', children }: { type?: 'info' | 'warning' | 'danger'; children: React.ReactNode }) {
  return <div className={`notice ${type}`}>{type === 'info' ? <Info size={17} /> : <AlertTriangle size={17} />}<div>{children}</div></div>
}

const BET_SIZE_OPTIONS = [25, 33, 50, 75, 100, 125]

function BetSizeSelector({ street, tree, onChange }: { street: Street; tree: TreeConfig; onChange: (tree: TreeConfig) => void }) {
  const selected = getBetSizes(tree, street)
  const toggle = (size: number) => {
    const next = selected.includes(size) ? selected.filter((value) => value !== size) : [...selected, size]
    if (next.length === 0 || next.length > 4) return
    onChange(setBetSizes(tree, street, next))
  }
  return <div className="bet-size-selector"><div><strong>{street.toUpperCase()}</strong><span>{selected.join(' / ')}% pot</span></div><div>{BET_SIZE_OPTIONS.map((size) => <button key={size} className={selected.includes(size) ? 'active' : ''} onClick={() => toggle(size)}>{size}%</button>)}</div></div>
}

function HomeScreen({ go }: { go: (screen: Screen) => void }) {
  return (
    <>
      <AppHeader title="GTO Lab" />
      <main className="page home-page">
        <section className="hero-card">
          <div className="hero-glow" />
          <div className="eyebrow"><Sparkles size={14} /> iPHONE SOLVER EXPERIMENT</div>
          <h1>ポケットの中で、<br /><em>1局面ずつ深く。</em></h1>
          <p>サーバーへ送らず、SafariのWebAssemblyだけで限定ポストフロップ局面を計算します。</p>
          <div className="hero-board">
            <CardChip card="As" /><CardChip card="7d" /><CardChip card="2c" />
            <span className="board-arrow">→</span>
            <div className="mini-strategy"><i style={{ width: '62%' }} /><i style={{ width: '38%' }} /></div>
          </div>
          <button className="primary-button hero-button" onClick={() => go('setup')}><Play size={19} fill="currentColor" />新しい局面を計算</button>
        </section>

        <section className="quick-grid">
          <button className="quick-card trainer-quick" onClick={() => go('trainer')}><span className="quick-icon blue"><Target size={21} /></span><strong>GTOトレーナー</strong><small>6-maxテーブルで3択以上を練習</small><ChevronRight size={18} /></button>
          <button className="quick-card" onClick={() => go('batch')}><span className="quick-icon amber"><Layers3 size={21} /></span><strong>一括事前計算</strong><small>複数サイズ局面をまとめて保存</small><ChevronRight size={18} /></button>
          <button className="quick-card" onClick={() => go('saved')}><span className="quick-icon green"><Save size={21} /></span><strong>保存済み結果</strong><small>端末内のソリューション</small><ChevronRight size={18} /></button>
          <button className="quick-card benchmark-quick" onClick={() => go('benchmark')}><span className="quick-icon amber"><Gauge size={21} /></span><strong>端末性能テスト</strong><small>WASM・メモリ・速度</small><ChevronRight size={18} /></button>
        </section>

        <section className="section-card capability-card">
          <div className="section-title"><BrainCircuit size={18} /><div><strong>現在できること</strong><span>初期実験版</span></div></div>
          <div className="capability-list">
            <div><Check size={15} /><span>本物のDCFRソルバー</span><small>推測値ではありません</small></div>
            <div><Check size={15} /><span>テーブル型3択以上トレーナー</span><small>サイズ・レイズ・All-in</small></div>
            <div><Check size={15} /><span>16-bit省メモリ計算</span><small>事前見積もり対応</small></div>
            <div><Check size={15} /><span>完全オフライン起動</span><small>PWA・IndexedDB</small></div>
          </div>
        </section>

        <Notice type="warning">
          <strong>実験的な計算環境です</strong>
          <p>大きなツリーではSafariが終了する可能性があります。最初はRiver Testを推奨します。</p>
        </Notice>
        <button className="text-link" onClick={() => go('license')}><BookOpen size={15} />ライセンスと計算上の制限</button>
      </main>
    </>
  )
}

function CardPicker({ selected, used, onSelect, onClose }: { selected: Card; used: Card[]; onSelect: (card: Card) => void; onClose: () => void }) {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="bottom-sheet card-picker" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="sheet-title"><div><strong>カードを選択</strong><span>現在: {displayCard(selected)}</span></div><button className="icon-button" onClick={onClose}><X size={19} /></button></div>
        <div className="card-picker-grid">
          {RANKS_DESC.split('').flatMap((rank) => SUITS.split('').map((suit) => {
            const card = `${rank}${suit}`
            const disabled = used.includes(card) && card !== selected
            return <button key={card} className={`${isRedCard(card) ? 'red' : ''} ${card === selected ? 'selected' : ''}`} disabled={disabled} onClick={() => onSelect(card)}>{displayCard(card)}</button>
          }))}
        </div>
      </div>
    </div>
  )
}

function SetupScreen({ onStart }: { onStart: () => Promise<void> }) {
  const { config, updateConfig, estimate, setEstimate, setError } = useAppStore()
  const [rangeSide, setRangeSide] = useState<'oop' | 'ip'>('oop')
  const [showGrid, setShowGrid] = useState(false)
  const [pickerIndex, setPickerIndex] = useState<number | null>(null)
  const [estimating, setEstimating] = useState(false)
  const cards = [...config.board.flop, ...(config.board.turn ? [config.board.turn] : []), ...(config.board.river ? [config.board.river] : [])]

  let rangeError: string | null = null
  try {
    validateBoard(cards)
    parseRange(config.ranges.oop.notation, cards)
    parseRange(config.ranges.ip.notation, cards)
  } catch (error) {
    rangeError = error instanceof Error ? error.message : '設定が不正です'
  }

  const estimateNow = async () => {
    if (rangeError) return
    setEstimating(true)
    setError(null)
    try {
      setEstimate(await solverClient.estimate(config))
    } catch (error) {
      const solverError = (error as { solverError?: SolverError }).solverError
      setError(solverError ?? { code: 'SOLVE_FAILED', message: error instanceof Error ? error.message : '見積もりに失敗しました' })
    } finally {
      setEstimating(false)
    }
  }

  const setCard = (index: number, card: Card) => {
    updateConfig((next) => {
      if (index < 3) next.board.flop[index] = card
      else if (index === 3) next.board.turn = card
      else next.board.river = card
      return next
    })
    setPickerIndex(null)
  }

  return (
    <>
      <AppHeader title="局面設定" back={() => useAppStore.getState().setScreen('home')} />
      <main className="page setup-page">
        <div className="step-indicator"><span className="active">1</span><i /><span>2</span><i /><span>3</span><small>SETUP</small><small>SOLVE</small><small>REVIEW</small></div>

        <section className="section-card">
          <div className="section-heading"><div><span className="number-badge">01</span><div><strong>計算プリセット</strong><small>まずはRiver Testがおすすめ</small></div></div></div>
          <div className="preset-list">
            {(Object.keys(presetMeta) as SolverPresetId[]).map((id) => {
              const meta = presetMeta[id]
              return <button key={id} className={config.preset === id ? 'selected' : ''} onClick={() => updateConfig((next) => applyPreset(next, id))}>
                <span>{id === 'river-test' ? <Zap size={18} /> : id === 'turn-test' ? <Layers3 size={18} /> : <BrainCircuit size={18} />}</span>
                <div><strong>{meta.name}</strong><small>{meta.short}</small></div>
                <em>{meta.stability}</em>
                <i>{config.preset === id && <Check size={15} />}</i>
              </button>
            })}
          </div>
        </section>

        <section className="section-card">
          <div className="section-heading"><div><span className="number-badge">02</span><div><strong>局面</strong><small>BTN vs BB · Single-raised pot</small></div></div></div>
          <div className="board-editor">
            <label>BOARD</label>
            <div>{cards.map((card, index) => <button key={`${index}-${card}`} onClick={() => setPickerIndex(index)}><CardChip card={card} /><small>{index < 3 ? 'FLOP' : index === 3 ? 'TURN' : 'RIVER'}</small></button>)}</div>
          </div>
          <div className="field-row two">
            <label><span>ポット</span><div><input type="number" min="0.5" step="0.5" value={config.potBb} onChange={(event) => updateConfig((next) => ({ ...next, potBb: Number(event.target.value) }))} /><em>bb</em></div></label>
            <label><span>残りスタック</span><div><input type="number" min="1" step="0.5" value={config.effectiveStackBb} onChange={(event) => updateConfig((next) => ({ ...next, effectiveStackBb: Number(event.target.value) }))} /><em>bb</em></div></label>
          </div>
          <div className="position-line"><span>OOP <strong>{config.positions.oop}</strong></span><i>VS</i><span>IP <strong>{config.positions.ip}</strong></span></div>
        </section>

        <section className="section-card">
          <div className="section-heading"><div><span className="number-badge">03</span><div><strong>プリフロップレンジ</strong><small>ボードカードは自動除外</small></div></div></div>
          <Notice><strong>サンプルレンジ</strong><p>動作確認用であり、完全なGTOレンジではありません。</p></Notice>
          <div className="segmented-control"><button className={rangeSide === 'oop' ? 'active' : ''} onClick={() => setRangeSide('oop')}>OOP · BB</button><button className={rangeSide === 'ip' ? 'active' : ''} onClick={() => setRangeSide('ip')}>IP · BTN</button></div>
          <div className="range-summary"><span>{config.ranges[rangeSide].label}</span><strong>{rangeError ? '—' : compactRangeSummary(config.ranges[rangeSide].notation, cards)}</strong></div>
          <textarea className="range-input" value={config.ranges[rangeSide].notation} onChange={(event) => updateConfig((next) => {
            next.ranges[rangeSide].notation = event.target.value
            return next
          })} spellCheck={false} aria-label={`${rangeSide} range`} />
          {rangeError && <p className="field-error"><AlertTriangle size={14} />{rangeError}</p>}
          <button className="secondary-button full" onClick={() => setShowGrid((value) => !value)}><BarChart3 size={17} />{showGrid ? 'グリッドを閉じる' : '13×13グリッドで編集'}</button>
          {showGrid && !rangeError && <div className="grid-editor"><p>セルをタップ: 0 → 25 → 50 → 75 → 100%</p><RangeGrid editable notation={config.ranges[rangeSide].notation} board={cards} onNotationChange={(notation) => updateConfig((next) => {
            next.ranges[rangeSide].notation = notation
            return next
          })} /></div>}
        </section>

        <section className="section-card">
          <div className="section-heading"><div><span className="number-badge">04</span><div><strong>アクションツリーと精度</strong><small>初期値は3択以上の実戦向け設定</small></div></div></div>
          <div className="action-tree-summary"><strong>{strategicChoiceLabel(config.tree)}</strong><span>サイズは各ストリート最大4つ</span></div>
          {(['flop', 'turn', 'river'] as Street[]).map((street) => <BetSizeSelector key={street} street={street} tree={config.tree} onChange={(tree) => updateConfig((next) => ({ ...next, tree }))} />)}
          <div className="toggle-row"><div><strong>レイズを許可</strong><small>コールだけでなくレイズサイズも比較</small></div><button className={config.tree.raises ? 'toggle active' : 'toggle'} onClick={() => updateConfig((next) => ({ ...next, tree: { ...next.tree, raises: !next.tree.raises } }))}><i /></button></div>
          <div className="toggle-row"><div><strong>All-inサイズ</strong><small>各ストリートの候補へ追加</small></div><button className={config.tree.allIn ? 'toggle active' : 'toggle'} onClick={() => updateConfig((next) => ({ ...next, tree: { ...next.tree, allIn: !next.tree.allIn } }))}><i /></button></div>
          <div className="select-row">
            <label><span>メモリモード</span><select value={config.memoryMode} onChange={(event) => updateConfig((next) => ({ ...next, memoryMode: event.target.value as typeof next.memoryMode }))}><option value="compact">省メモリ (16-bit)</option><option value="standard">標準 (32-bit)</option><option value="high">高精度 (32-bit)</option></select></label>
            <label><span>最大反復</span><select value={config.accuracy.maxIterations} onChange={(event) => updateConfig((next) => ({ ...next, accuracy: { ...next.accuracy, maxIterations: Number(event.target.value) } }))}><option value="20">20 · 確認</option><option value="60">60 · 軽量</option><option value="100">100 · 標準</option><option value="250">250 · 深め</option></select></label>
          </div>
          <label className="memory-limit"><span>アプリ内メモリ上限 <strong>{config.memoryLimitMb}MB</strong></span><input type="range" min="96" max="768" step="32" value={config.memoryLimitMb} onChange={(event) => updateConfig((next) => ({ ...next, memoryLimitMb: Number(event.target.value) }))} /></label>
        </section>

        <section className={`estimate-card ${estimate?.rating ?? ''}`}>
          <div><MemoryStick size={22} /><span><small>ソルバー実測見積もり</small><strong>{estimating ? '測定中…' : estimate ? formatBytes(estimate.bytes) : '未測定'}</strong></span>{estimate && <em>{estimate.ratingLabel}</em>}</div>
          {estimate && <div className="estimate-split"><span>16-bit <strong>{formatBytes(estimate.compressedBytes)}</strong></span><span>32-bit <strong>{formatBytes(estimate.uncompressedBytes)}</strong></span></div>}
          <button className="secondary-button full" disabled={!!rangeError || estimating} onClick={estimateNow}><Gauge size={17} />正確なメモリを見積もる</button>
        </section>

        <button className="primary-button sticky-action" disabled={!!rangeError || estimating || estimate?.rating === 'not-recommended'} onClick={onStart}><Play size={19} fill="currentColor" />{estimate ? 'この設定で計算開始' : '見積もって計算開始'}</button>
      </main>
      {pickerIndex !== null && <CardPicker selected={cards[pickerIndex]} used={cards} onSelect={(card) => setCard(pickerIndex, card)} onClose={() => setPickerIndex(null)} />}
    </>
  )
}

function BatchScreen() {
  const [streetMix, setStreetMix] = useState<BatchStreetMix>('river')
  const [actionSet, setActionSet] = useState<BatchActionSet>('strategic')
  const [count, setCount] = useState(5)
  const [iterations, setIterations] = useState(60)
  const [memoryLimitMb, setMemoryLimitMb] = useState(320)
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  const [interrupted, setInterrupted] = useState(false)
  const [completed, setCompleted] = useState(0)
  const [failed, setFailed] = useState(0)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [currentConfig, setCurrentConfig] = useState<ReturnType<typeof createBatchPlan>[number] | null>(null)
  const [solverProgress, setSolverProgress] = useState<SolverProgress | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [savedCount, setSavedCount] = useState(0)
  const stopRequested = useRef(false)
  const runningRef = useRef(false)
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null)

  useEffect(() => {
    listSolverResults().then((results) => setSavedCount(results.length)).catch(() => undefined)
    return () => {
      if (runningRef.current) solverClient.cancel()
    }
  }, [])

  const stop = () => {
    stopRequested.current = true
    setInterrupted(true)
    solverClient.cancel()
  }

  const startBatch = async () => {
    const plan = createBatchPlan({ count, streetMix, maxIterations: iterations, memoryLimitMb, actionSet })
    stopRequested.current = false
    setInterrupted(false)
    runningRef.current = true
    setRunning(true)
    setFinished(false)
    setCompleted(0)
    setFailed(0)
    setMessage(null)
    try {
      try {
        if ('wakeLock' in navigator) wakeLockRef.current = await (navigator as Navigator & { wakeLock: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock.request('screen')
      } catch { /* Wake Lock is best-effort. */ }

      for (let index = 0; index < plan.length; index += 1) {
        if (stopRequested.current) break
        const config = plan[index]
        setCurrentIndex(index)
        setCurrentConfig(config)
        setSolverProgress(null)
        try {
          const result = await solverClient.solve(config, setSolverProgress)
          if (stopRequested.current) break
          await saveSolverResult(result)
          setCompleted((value) => value + 1)
          setSavedCount((value) => value + 1)
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') break
          setFailed((value) => value + 1)
          setMessage(error instanceof Error ? error.message : '局面の計算に失敗しました')
        }
      }
    } finally {
      solverClient.terminate()
      runningRef.current = false
      setRunning(false)
      setFinished(true)
      setCurrentConfig(null)
      setSolverProgress(null)
      await wakeLockRef.current?.release().catch(() => undefined)
      wakeLockRef.current = null
    }
  }

  const overallProgress = running
    ? ((completed + failed + (solverProgress?.progress ?? 0)) / count) * 100
    : finished ? ((completed + failed) / count) * 100 : 0

  return (
    <>
      <AppHeader title="一括事前計算" back={() => { if (running) stop(); useAppStore.getState().setScreen('home') }} />
      <main className="page batch-page">
        <section className="batch-hero">
          <Layers3 size={34} />
          <div className="eyebrow">LOCAL SOLVE QUEUE</div>
          <h1>色んな局面を先に計算し、<br /><em>問題プールを育てる。</em></h1>
          <p>ポジション、SRP / 3-bet pot、ボードテクスチャを分散し、実WASMソルバーで1局面ずつ計算して端末へ保存します。</p>
        </section>

        {!running && <>
          <section className="section-card batch-options">
            <div className="section-title"><Layers3 size={18} /><div><strong>計算するセット</strong><span>最大20局面・途中保存</span></div></div>
            <label><span>ストリート構成</span><select value={streetMix} onChange={(event) => setStreetMix(event.target.value as BatchStreetMix)}><option value="river">Riverのみ（最速）</option><option value="turn-river">Turn / River</option><option value="mixed">Flop / Turn / River</option></select></label>
            <label><span>アクション構成</span><select value={actionSet} onChange={(event) => setActionSet(event.target.value as BatchActionSet)}><option value="strategic">3択以上 · 複数サイズ＋レイズ＋All-in</option><option value="lite">軽量 · 1サイズ（旧方式）</option></select></label>
            <label><span>局面数</span><select value={count} onChange={(event) => setCount(Number(event.target.value))}><option value="3">3局面 · 動作確認</option><option value="5">5局面 · おすすめ</option><option value="10">10局面 · しっかり</option><option value="20">20局面 · 長時間</option></select></label>
            <label><span>各局面の反復</span><select value={iterations} onChange={(event) => setIterations(Number(event.target.value))}><option value="20">20 · 軽い下準備</option><option value="60">60 · おすすめ</option><option value="100">100 · 標準</option></select></label>
            <label className="memory-limit"><span>1局面のメモリ上限 <strong>{memoryLimitMb}MB</strong></span><input type="range" min="128" max="768" step="32" value={memoryLimitMb} onChange={(event) => setMemoryLimitMb(Number(event.target.value))} /></label>
            <div className="batch-plan-summary"><div><span>構成</span><strong>{batchMixLabel(streetMix)}</strong></div><div><span>選択肢</span><strong>{actionSet === 'strategic' ? '3〜5択' : '2択中心'}</strong></div><div><span>保存済み</span><strong>{savedCount}局面</strong></div></div>
          </section>
          {actionSet === 'strategic' && <Notice><strong>既存結果は消えません</strong><p>新しい複数サイズの結果を別データとして追加します。トレーナーは3択以上の新結果を優先するため、上書きや全削除は不要です。</p></Notice>}
          {streetMix === 'mixed' && <Notice type="warning"><strong>Flopを含むため重くなります</strong><p>iPhoneでは最初に5局面以下を推奨します。Safariを開いたまま、充電しながらの長時間実行は発熱に注意してください。</p></Notice>}
          {finished && <Notice type={failed > 0 || interrupted ? 'warning' : 'info'}><strong>{interrupted ? '一括計算を中断しました' : '一括計算が完了しました'}</strong><p>{completed}局面を保存、{failed}局面が失敗しました。完了済みの結果はそのままトレーナーで使えます。</p></Notice>}
          <button className="primary-button batch-start" onClick={startBatch}><Play size={19} fill="currentColor" />{finished ? '別のセットを計算' : `${count}局面の事前計算を開始`}</button>
          {finished && completed > 0 && <button className="secondary-button full" onClick={() => useAppStore.getState().setScreen('trainer')}><Target size={18} />ランダムトレーニングへ</button>}
        </>}

        {running && currentConfig && <>
          <section className="batch-running">
            <div className="batch-overall"><span>全体 {currentIndex + 1} / {count}</span><strong>{Math.round(overallProgress)}%</strong></div>
            <i><span style={{ width: `${Math.min(100, overallProgress)}%` }} /></i>
            <div className="batch-current-board">{[...currentConfig.board.flop, ...(currentConfig.board.turn ? [currentConfig.board.turn] : []), ...(currentConfig.board.river ? [currentConfig.board.river] : [])].map((card) => <CardChip small card={card} key={card} />)}</div>
            <strong>{currentConfig.trainingContext?.spotLabel}</strong>
            <small>{presetMeta[currentConfig.preset].name} · {strategicChoiceLabel(currentConfig.tree)} · {solverProgress ? phaseLabels[solverProgress.phase] : 'ゲームツリーを準備中'}</small>
          </section>
          <section className="solve-stats batch-stats">
            <div><Activity size={18} /><span>反復</span><strong>{solverProgress?.iteration ?? 0}<small> / {iterations}</small></strong></div>
            <div><BrainCircuit size={18} /><span>Exploitability</span><strong>{solverProgress?.exploitability !== undefined ? `${solverProgress.exploitability.toFixed(3)}bb` : '—'}</strong></div>
            <div><Save size={18} /><span>保存完了</span><strong>{completed}<small> / {count}</small></strong></div>
            <div><Clock3 size={18} /><span>現在の経過</span><strong>{formatTime(solverProgress?.elapsedMs)}</strong></div>
          </section>
          {message && <Notice type="warning"><strong>直前の局面をスキップ</strong><p>{message}</p></Notice>}
          <Notice type="warning"><strong>Safariを開いたままにしてください</strong><p>バックグラウンドへ移ると計算が停止する場合があります。途中停止しても完了済み結果は残ります。</p></Notice>
          <button className="stop-button" onClick={stop}><CircleStop size={20} />一括計算を停止</button>
        </>}
      </main>
    </>
  )
}

function SolvingScreen({ stop }: { stop: () => void }) {
  const { progress, config, estimate } = useAppStore()
  const ratio = progress?.progress ?? 0
  const etaSeconds = progress?.iterationsPerSecond ? Math.max(0, (config.accuracy.maxIterations - progress.iteration) / progress.iterationsPerSecond) : null
  return (
    <main className="solving-page">
      <div className="solving-top"><div className="brand-mark">G</div><span>LOCAL SOLVER RUNNING</span><i /></div>
      <section className="solve-hero">
        <div className="solver-orbit" style={{ '--progress': `${ratio * 360}deg` } as React.CSSProperties}>
          <div><strong>{Math.round(ratio * 100)}<small>%</small></strong><span>{progress ? phaseLabels[progress.phase] : '準備中'}</span></div>
        </div>
        <div className="solve-board">{[...config.board.flop, ...(config.board.turn ? [config.board.turn] : []), ...(config.board.river ? [config.board.river] : [])].map((card) => <CardChip small card={card} key={card} />)}</div>
        <h1>{progress ? phaseLabels[progress.phase] : 'WASMを初期化中'}</h1>
        <p>計算中はSafariを開いたままにしてください</p>
      </section>

      <section className="solve-stats">
        <div><Activity size={18} /><span>反復回数</span><strong>{progress?.iteration ?? 0}<small> / {config.accuracy.maxIterations}</small></strong></div>
        <div><BrainCircuit size={18} /><span>Exploitability</span><strong>{progress?.exploitability !== undefined ? `${progress.exploitability.toFixed(3)}bb` : '—'}</strong></div>
        <div><Clock3 size={18} /><span>経過時間</span><strong>{formatTime(progress?.elapsedMs)}</strong></div>
        <div><Gauge size={18} /><span>最大残り目安</span><strong>{etaSeconds === null ? '算出中' : formatTime(etaSeconds * 1000)}</strong></div>
        <div><MemoryStick size={18} /><span>推定メモリ</span><strong>{formatBytes(progress?.estimatedMemoryBytes ?? estimate?.bytes)}</strong></div>
        <div><Zap size={18} /><span>計算速度</span><strong>{progress?.iterationsPerSecond ? `${progress.iterationsPerSecond.toFixed(2)} it/s` : '—'}</strong></div>
      </section>

      {progress?.speedDropPercent !== undefined && progress.speedDropPercent >= 25 && <Notice type="warning"><strong>速度が開始時より{Math.round(progress.speedDropPercent)}%低下</strong><p>端末が発熱している可能性があります。必要なら中断してください。</p></Notice>}
      <Notice type="warning"><strong>画面ロック・アプリ切替に注意</strong><p>iOSがWorkerを停止する場合があります。バックグラウンド計算には対応していません。</p></Notice>
      <button className="stop-button" onClick={stop}><CircleStop size={20} />計算を停止</button>
      <small className="solve-footnote">停止時はWorkerを破棄し、不完全な結果を保存しません。</small>
    </main>
  )
}

function ActionBars({ node, combo }: { node: StrategyNode; combo?: string }) {
  const comboStrategy = combo ? node.rangeStrategy.combos[combo] : null
  return <div className="action-bars">{node.actions.map((action) => {
    const detail = comboStrategy?.actions.find((candidate) => candidate.actionId === action.id)
    const frequency = detail?.frequency ?? action.frequency
    const ev = detail?.ev ?? action.ev
    return <div className={`action-bar ${action.kind}`} key={action.id}><div><span><i />{action.label}</span><strong>{Math.round(frequency * 100)}%</strong></div><div className="bar-track"><i style={{ width: `${Math.max(1, frequency * 100)}%` }} /></div>{ev !== undefined && <small>EV {ev.toFixed(3)}bb</small>}</div>
  })}</div>
}

const resultTabs: Array<{ id: ResultTab; label: string }> = [
  { id: 'summary', label: '概要' }, { id: 'range', label: 'レンジ' }, { id: 'combo', label: 'コンボ' }, { id: 'explanation', label: '説明' }, { id: 'tree', label: 'ツリー' },
]

function ResultScreen() {
  const { result, resultTab, setResultTab, selectedNodeId, setSelectedNode, selectedCombo, setSelectedCombo, setTrainerSourceResult } = useAppStore()
  const [rangeActionId, setRangeActionId] = useState<string | null>(null)
  if (!result) return null
  const node = result.nodes[selectedNodeId ?? result.defaultNodeId] ?? result.nodes[result.rootNodeId]
  const rangeAction = node.actions.find((action) => action.id === rangeActionId) ?? node.actions[0]
  const pickCombo = (combo: string) => {
    setSelectedCombo(combo)
    setResultTab('combo')
  }
  const explanation = selectedCombo ? explainCombo(node, selectedCombo) : []

  return (
    <>
      <AppHeader title="計算結果" back={() => useAppStore.getState().setScreen('home')} />
      <main className="page result-page">
        <section className="result-spot">
          <div><span>{result.config.positions.oop}</span><strong>vs</strong><span>{result.config.positions.ip}</span><em>{node.street.toUpperCase()}</em></div>
          <div className="result-board">{node.board.map((card) => <CardChip small card={card} key={card} />)}</div>
          <small>{node.label} · Pot {node.potBb.toFixed(1)}bb</small>
        </section>
        <div className="result-tabs">{resultTabs.map((tab) => <button key={tab.id} className={resultTab === tab.id ? 'active' : ''} onClick={() => setResultTab(tab.id)}>{tab.label}</button>)}</div>

        {resultTab === 'summary' && <>
          <section className="metric-grid">
            <div><Clock3 size={17} /><span>計算時間</span><strong>{formatTime(result.metadata.durationMs)}</strong></div>
            <div><Activity size={17} /><span>反復回数</span><strong>{result.metadata.iterations}</strong></div>
            <div><BrainCircuit size={17} /><span>Exploitability</span><strong>{result.metadata.exploitability?.toFixed(3) ?? '—'}bb</strong></div>
            <div><MemoryStick size={17} /><span>推定メモリ</span><strong>{formatBytes(result.metadata.estimatedMemoryBytes)}</strong></div>
          </section>
          <section className="section-card"><div className="section-title"><BarChart3 size={18} /><div><strong>{node.actingPlayer} レンジ全体</strong><span>到達ウェイトで集計</span></div></div><ActionBars node={node} /></section>
          <button className="primary-button trainer-result-link" onClick={() => { setTrainerSourceResult(result.id); useAppStore.getState().setScreen('trainer') }}><Target size={18} />この結果で1問トレーニング</button>
          <Notice><strong>計算済みの実データ</strong><p>表示値はDCFR計算の出力です。サンプルレンジ自体は完全なGTOプリフロップレンジではありません。</p></Notice>
        </>}

        {resultTab === 'range' && <section className="section-card range-result-card">
          <div className="section-title"><BarChart3 size={18} /><div><strong>13×13 レンジ</strong><span>セルをタップしてコンボ詳細へ</span></div></div>
          <div className="action-filter">{node.actions.map((action) => <button key={action.id} className={`${action.kind} ${rangeAction.id === action.id ? 'active' : ''}`} onClick={() => setRangeActionId(action.id)}>{action.label}</button>)}</div>
          <div className="legend-line"><span>0%</span><i className={rangeAction.kind} /><span>100%</span><em>セル内の到達コンボ加重平均</em></div>
          <RangeGrid node={node} action={rangeAction} onComboSelect={pickCombo} />
        </section>}

        {resultTab === 'combo' && <section className="section-card combo-card">
          {selectedCombo ? <>
            <div className="combo-header"><div><strong>{displayCard(selectedCombo.slice(0, 2))} {displayCard(selectedCombo.slice(2, 4))}</strong><span>{node.actingPlayer} · {node.street}</span></div><button className="secondary-button" onClick={() => setResultTab('range')}>別のコンボ</button></div>
            <ActionBars node={node} combo={selectedCombo} />
            <div className="ev-note"><Info size={15} />EV差が小さいアクションは、頻度が偏っていても実戦上の損失が小さい場合があります。</div>
          </> : <div className="empty-state"><BarChart3 size={36} /><strong>コンボを選択</strong><p>レンジタブのセルをタップすると、個別の頻度とEVを表示します。</p><button className="primary-button" onClick={() => setResultTab('range')}>レンジを開く</button></div>}
        </section>}

        {resultTab === 'explanation' && <section className="section-card explanation-card">
          {selectedCombo ? <>
            <div className="section-title"><Sparkles size={18} /><div><strong>{selectedCombo} の簡易説明</strong><span>API不使用・ルールベース</span></div></div>
            {explanation.map((section) => <article key={section.title}><div><strong>{section.title}</strong><em className={section.certainty}>{section.certainty === 'fact' ? '確定値' : section.certainty === 'interpretation' ? '解釈' : '推定'}</em></div><p>{section.body}</p></article>)}
          </> : <div className="empty-state"><Sparkles size={36} /><strong>説明するコンボを選択</strong><p>先にレンジからコンボを選んでください。</p><button className="primary-button" onClick={() => setResultTab('range')}>レンジを開く</button></div>}
        </section>}

        {resultTab === 'tree' && <section className="section-card tree-card">
          <div className="section-title"><Layers3 size={18} /><div><strong>取得済み戦略ノード</strong><span>同一ストリート内を表示</span></div></div>
          {Object.values(result.nodes).map((candidate) => <button key={candidate.id} className={candidate.id === node.id ? 'active' : ''} onClick={() => { setSelectedNode(candidate.id); setRangeActionId(null) }}><span>{candidate.street.toUpperCase()}</span><div><strong>{candidate.label}</strong><small>{candidate.actingPlayer} · {candidate.actions.map((action) => action.label).join(' / ')}</small></div><ChevronRight size={17} /></button>)}
          <Notice><strong>初期版のツリービュー</strong><p>現在は各チャンスカードの全ノードを保存せず、ルート付近の意思決定ノードのみ抽出します。</p></Notice>
        </section>}
      </main>
    </>
  )
}

function TrainerScreen() {
  const { setTrainerSourceResult } = useAppStore()
  const [items, setItems] = useState<SolverResult[]>([])
  const [selectedResultId, setSelectedResultId] = useState('')
  const [sourceMode, setSourceMode] = useState<TrainerSourceMode>('mixed')
  const [sessionLength, setSessionLength] = useState(10)
  const [progress, setProgress] = useState<TrainerProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [deck, setDeck] = useState<TrainerQuestion[]>([])
  const [questionIndex, setQuestionIndex] = useState(0)
  const [evaluation, setEvaluation] = useState<TrainerEvaluation | null>(null)
  const [answers, setAnswers] = useState<TrainerEvaluation[]>([])
  const [complete, setComplete] = useState(false)

  useEffect(() => {
    Promise.all([listSolverResults(), getTrainerProgress()])
      .then(([results, storedProgress]) => {
        setItems(results)
        const trainerSourceResultId = useAppStore.getState().trainerSourceResultId
        const preferred = results.some((result) => result.id === trainerSourceResultId) ? trainerSourceResultId : results[0]?.id
        setSelectedResultId(preferred ?? '')
        setProgress(storedProgress)
      })
      .finally(() => setLoading(false))
  }, [])

  const selectedResult = items.find((item) => item.id === selectedResultId) ?? items[0]
  const question = deck[questionIndex]
  const questionResult = question?.kind === 'postflop' ? items.find((item) => item.id === question.resultId) : undefined
  const node = question?.kind === 'postflop' && questionResult ? questionResult.nodes[question.nodeId] : undefined

  const startSession = () => {
    const questions = createTrainerDeck({
      results: items,
      mode: sourceMode,
      count: sessionLength,
      selectedResultId: selectedResult?.id,
      recentQuestionIds: progress?.recentQuestionIds,
    })
    if (questions.length === 0) return
    setDeck(questions)
    setQuestionIndex(0)
    setEvaluation(null)
    setAnswers([])
    setComplete(false)
  }

  const answer = (actionId: string) => {
    if (!question || evaluation) return
    const next = question.kind === 'preflop'
      ? evaluatePreflopAnswer(question, actionId)
      : node ? evaluateTrainerAnswer(node, question.combo, actionId) : null
    if (!next) return
    setEvaluation(next)
    setAnswers((current) => [...current, next])
    recordTrainerAnswer(
      next.verdict,
      next.evLossBb,
      trainerQuestionCategory(question, questionResult, node),
      question.id,
    ).then(setProgress).catch(() => undefined)
  }

  const nextQuestion = async () => {
    if (questionIndex + 1 >= deck.length) {
      setComplete(true)
      setProgress(await recordTrainerSessionComplete())
      return
    }
    setQuestionIndex((current) => current + 1)
    setEvaluation(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const resetToIntro = () => {
    setDeck([])
    setQuestionIndex(0)
    setEvaluation(null)
    setAnswers([])
    setComplete(false)
  }

  if (loading) {
    return <><AppHeader title="GTOトレーナー" back={() => useAppStore.getState().setScreen('home')} /><main className="page trainer-page"><div className="loading-line">問題を準備中…</div></main></>
  }

  if (deck.length === 0) {
    const pool = trainerPoolStats(items)
    const selectedQuestions = selectedResult ? buildTrainerQuestions(selectedResult).length : 0
    const availableQuestions = sourceMode === 'preflop'
      ? pool.preflop
      : sourceMode === 'postflop' ? pool.postflop
        : sourceMode === 'single-result' ? selectedQuestions
          : pool.preflop + pool.postflop
    const overallAccuracy = progress?.totalAnswers
      ? Math.round(((progress.correctAnswers + progress.closeAnswers) / progress.totalAnswers) * 100)
      : 0
    return (
      <>
        <AppHeader title="GTOトレーナー" back={() => useAppStore.getState().setScreen('home')} />
        <main className="page trainer-page">
          <section className="trainer-intro">
            <span className="trainer-target"><Target size={30} /></span>
            <div className="eyebrow">RANDOM ACTION TRAINER</div>
            <h1>プリフロップからリバーまで、<br /><em>毎回ちがう問題。</em></h1>
            <p>保存済みソルバー結果を横断し、3択以上ある難しめの局面と境界ハンドを優先して1問ずつ出題します。</p>
          </section>

          <section className="section-card trainer-source">
            <div className="section-title"><Database size={18} /><div><strong>出題範囲</strong><span>練習したい範囲を選択</span></div></div>
            <div className="trainer-mode-grid">
              {([
                ['mixed', '全部ランダム', 'Preflop〜River'],
                ['preflop', 'プリフロップ', `${pool.preflop.toLocaleString()}問`],
                ['postflop', 'ポストフロップ', `${pool.strategicResults}/${pool.results}局面が3択以上`],
                ['single-result', '1局面を深掘り', '保存結果を選択'],
              ] as Array<[TrainerSourceMode, string, string]>).map(([mode, label, detail]) => <button key={mode} className={sourceMode === mode ? 'active' : ''} onClick={() => setSourceMode(mode)}><strong>{label}</strong><small>{detail}</small></button>)}
            </div>
            {sourceMode === 'single-result' && (items.length > 0
              ? <select value={selectedResult?.id ?? ''} onChange={(event) => { setSelectedResultId(event.target.value); setTrainerSourceResult(event.target.value) }}>
                {items.map((result) => <option value={result.id} key={result.id}>{result.config.trainingContext?.spotLabel ?? `${result.config.positions.oop} vs ${result.config.positions.ip}`} · {presetMeta[result.config.preset].name} · {new Date(result.createdAt).toLocaleDateString('ja-JP')}</option>)}
              </select>
              : <Notice type="warning"><strong>保存済み局面がありません</strong><p>一括事前計算で問題プールを作成してください。</p></Notice>)}
            <div className="trainer-session-options">
              <label><span>1セッション</span><select value={sessionLength} onChange={(event) => setSessionLength(Number(event.target.value))}><option value="10">10問</option><option value="20">20問</option><option value="50">50問</option></select></label>
              <div><span>出題プール</span><strong>{availableQuestions.toLocaleString()}問</strong><small>直近120問はできるだけ回避</small></div>
            </div>
            <div className="trainer-source-summary"><div><strong>Preflop {pool.preflop.toLocaleString()}問</strong><small>全問題3択以上</small></div><div><strong>Postflop {pool.postflop.toLocaleString()}問</strong><small>3択以上 · {pool.strategicResults}局面</small></div></div>
            {pool.legacyPostflop > 0 && <div className="legacy-pool-note"><span>旧2択問題 {pool.legacyPostflop.toLocaleString()}問は通常出題から除外</span><strong>再計算すれば自動で追加</strong></div>}
          </section>

          {progress && progress.totalAnswers > 0 && <section className="trainer-history">
            <div><span>累計回答</span><strong>{progress.totalAnswers}</strong></div>
            <div><span>良い選択</span><strong>{overallAccuracy}%</strong></div>
            <div><span>Postflop平均EV損失</span><strong>{progress.evMeasuredAnswers ? `${(progress.totalEvLossBb / progress.evMeasuredAnswers).toFixed(3)}bb` : '—'}</strong></div>
          </section>}

          {availableQuestions > 0
            ? <button className="primary-button trainer-start" onClick={startSession}><Play size={19} fill="currentColor" />{sessionLength}問ランダムトレーニングを始める</button>
            : <Notice type="warning"><strong>ポストフロップ問題がありません</strong><p>一括事前計算で複数局面を作るか、プリフロップを選択してください。</p></Notice>}
          {(sourceMode === 'postflop' || sourceMode === 'single-result') && <button className="secondary-button full trainer-batch-link" onClick={() => useAppStore.getState().setScreen('batch')}><Layers3 size={18} />問題プールを一括事前計算</button>}
          {(sourceMode === 'mixed' || sourceMode === 'preflop') && <Notice type="warning"><strong>プリフロップは学習用ベースライン</strong><p>100bb・6-max・レーキなしの透明な基準表で、プリフロップ専用ソルバーの出力ではありません。ポストフロップだけが実WASMのEV計算結果です。</p></Notice>}
          <Notice><strong>難しさと混合戦略を両立</strong><p>ポストフロップは3択以上のノードを使い、混合頻度・EV差が近い問題を優先します。低頻度でも損失が小さいアクションは正解またはほぼ正解です。</p></Notice>
        </main>
      </>
    )
  }

  if (complete) {
    const exact = answers.filter((item) => item.verdict === 'correct').length
    const close = answers.filter((item) => item.verdict === 'close').length
    const evLoss = answers.reduce((sum, item) => sum + (item.evLossBb ?? 0), 0)
    const evMeasured = answers.filter((item) => item.evLossBb !== undefined).length
    return (
      <>
        <AppHeader title="セッション完了" back={resetToIntro} />
        <main className="page trainer-page">
          <section className="trainer-complete">
            <span><Trophy size={35} /></span>
            <div className="eyebrow">SESSION COMPLETE</div>
            <h1>{exact + close}<small> / {answers.length}</small></h1>
            <strong>良い選択</strong>
            <p>正解 {exact}問 · ほぼ正解 {close}問 · 要改善 {answers.length - exact - close}問</p>
          </section>
          <section className="metric-grid">
            <div><Target size={17} /><span>Postflop合計EV損失</span><strong>{evMeasured ? `${evLoss.toFixed(3)}bb` : '—'}</strong></div>
            <div><Activity size={17} /><span>Postflop 1問平均</span><strong>{evMeasured ? `${(evLoss / evMeasured).toFixed(3)}bb` : '—'}</strong></div>
          </section>
          <button className="primary-button trainer-start" onClick={startSession}><Play size={19} fill="currentColor" />もう{sessionLength}問解く</button>
          <button className="secondary-button full" onClick={resetToIntro}>出題元を変更</button>
        </main>
      </>
    )
  }

  if (!question) return null
  if (question.kind === 'postflop' && (!questionResult || !node)) return null
  const questionActions = question.kind === 'preflop' ? question.actions : node!.actions
  const explanation = question.kind === 'postflop' ? explainCombo(node!, question.combo) : []
  const verdictTitle = evaluation?.verdict === 'correct' ? '正解です' : evaluation?.verdict === 'close' ? 'ほぼ正解です' : 'ここは改善できます'
  const heroPosition = question.kind === 'preflop'
    ? question.heroPosition
    : node!.actingPlayer === 'OOP' ? questionResult!.config.positions.oop : questionResult!.config.positions.ip
  const opponentPosition = question.kind === 'preflop'
    ? question.villainPosition
    : node!.actingPlayer === 'OOP' ? questionResult!.config.positions.ip : questionResult!.config.positions.oop
  const tablePlayers: PokerTablePlayer[] = [
    { position: heroPosition, stackBb: question.kind === 'preflop' ? question.effectiveStackBb : node!.effectiveStackBb, active: true, acting: true, hero: true },
    ...(opponentPosition ? [{ position: opponentPosition, stackBb: question.kind === 'preflop' ? question.effectiveStackBb : node!.effectiveStackBb, active: true }] : []),
  ]
  const situationLabel = question.kind === 'preflop'
    ? question.spotLabel
    : questionResult!.config.trainingContext?.spotLabel ?? `${questionResult!.config.positions.oop} vs ${questionResult!.config.positions.ip}`
  const actionHistory = question.kind === 'preflop' ? question.actionHistory : node!.label

  return (
    <>
      <AppHeader title="GTOトレーナー" back={resetToIntro} />
      <main className="page trainer-page">
        <div className="trainer-progress"><div><span>QUESTION {questionIndex + 1}</span><strong>{questionIndex + 1} / {deck.length}</strong></div><i><span style={{ width: `${((questionIndex + (evaluation ? 1 : 0)) / deck.length) * 100}%` }} /></i></div>

        <section className="section-card trainer-situation">
          <div className="trainer-situation-head"><div><strong>{situationLabel}</strong><span>Hero {heroPosition} · {actionHistory}</span></div><em>{question.kind === 'preflop' ? 'PREFLOP' : node!.street.toUpperCase()}</em></div>
          <PokerTable
            players={tablePlayers}
            heroPosition={heroPosition}
            heroCards={[question.combo.slice(0, 2), question.combo.slice(2, 4)]}
            board={question.kind === 'postflop' ? node!.board : []}
            potBb={question.kind === 'preflop' ? question.potBb : node!.potBb}
            street={question.kind === 'preflop' ? 'preflop' : node!.street}
            actionHistory={actionHistory}
          />
          <h1 className="trainer-question-title">{question.kind === 'preflop' ? 'プリフロップでどうする？' : 'あなたならどうする？'}</h1>
        </section>

        <div className="trainer-choice-note"><span>ベットサイズも別アクションとして回答</span><strong>{questionActions.length}択</strong></div>
        <div className="trainer-actions">
          {questionActions.map((action) => {
            const selected = evaluation?.selected.action.id === action.id
            const benchmark = evaluation?.benchmark.action.id === action.id
            return <button key={action.id} disabled={!!evaluation} className={`${action.kind} ${selected ? 'selected' : ''} ${evaluation && benchmark ? 'benchmark' : ''}`} onClick={() => answer(action.id)}><i /><span>{trainerActionLabel(action)}</span>{evaluation && benchmark ? <small>{question.kind === 'preflop' ? '主戦略' : 'EV最大'}</small> : <ChevronRight size={18} />}</button>
          })}
        </div>

        {evaluation && <section className={`trainer-feedback ${evaluation.verdict}`}>
          <div className="feedback-title"><span>{evaluation.verdict === 'correct' ? <Check size={20} /> : <Target size={20} />}</span><div><strong>{verdictTitle}</strong><small>選択: {trainerActionLabel(evaluation.selected.action)}</small></div><em>{evaluation.evLossBb === undefined ? '頻度基準' : `−${evaluation.evLossBb.toFixed(3)}bb`}</em></div>
          <div className="feedback-summary">
            <div><span>主戦略</span><strong>{trainerActionLabel(evaluation.mainStrategy.action)}</strong><small>{Math.round(evaluation.mainStrategy.frequency * 100)}%</small></div>
            <div><span>選択の頻度</span><strong>{Math.round(evaluation.selected.frequency * 100)}%</strong><small>{evaluation.selected.ev === undefined ? (question.kind === 'preflop' ? '基準レンジ頻度' : 'EV —') : `EV ${evaluation.selected.ev.toFixed(3)}bb`}</small></div>
          </div>
          <div className="feedback-actions">{evaluation.actions.map((detail) => <div key={detail.action.id} className={detail.action.kind}><span><i />{trainerActionLabel(detail.action)}</span><strong>{Math.round(detail.frequency * 100)}%</strong><small>{detail.ev === undefined ? (question.kind === 'preflop' ? '基準頻度' : 'EV —') : `${detail.ev.toFixed(3)}bb`}</small></div>)}</div>
          {question.kind === 'preflop' ? <>
            <article><strong>この基準の考え方</strong><p>{question.rationale}</p></article>
            <article><strong>データの性質</strong><p>{question.sourceLabel}。プリフロップ専用ソルバーのEV出力ではないため、頻度境界は絶対値ではなく学習基準として扱ってください。</p></article>
          </> : explanation.slice(0, 3).map((section) => <article key={section.title}><strong>{section.title}</strong><p>{section.body}</p></article>)}
          <button className="primary-button full" onClick={nextQuestion}>{questionIndex + 1 >= deck.length ? '結果を見る' : '次の問題'}<ChevronRight size={18} /></button>
        </section>}
      </main>
    </>
  )
}

function SavedScreen() {
  const [items, setItems] = useState<SolverResult[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [streetFilter, setStreetFilter] = useState<'all' | Street>('all')
  const [choiceFilter, setChoiceFilter] = useState<'all' | 'strategic' | 'legacy'>('all')
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  useEffect(() => { listSolverResults().then(setItems).finally(() => setLoading(false)) }, [])
  const open = (result: SolverResult) => {
    useAppStore.getState().setResult(result)
    useAppStore.getState().setScreen('result')
  }
  const remove = async (id: string) => {
    if (!window.confirm('この計算結果を端末から削除しますか？')) return
    await deleteSolverResult(id)
    setItems((current) => current.filter((item) => item.id !== id))
  }
  const recalculate = (result: SolverResult) => {
    useAppStore.getState().setConfig(createRecalculationConfig(result.config))
    useAppStore.getState().setScreen('setup')
  }
  const annotated = useMemo(() => items.map((result) => {
    const profile = resultTrainingProfile(result)
    const street = result.nodes[result.rootNodeId]?.street ?? (result.config.board.river ? 'river' : result.config.board.turn ? 'turn' : 'flop')
    const spot = result.config.trainingContext?.spotLabel ?? `${result.config.positions.oop} vs ${result.config.positions.ip}`
    return { result, profile, street, spot, groupKey: `${spot}::${street}` }
  }), [items])
  const strategicCount = annotated.filter((item) => item.profile.questions > 0).length
  const normalizedQuery = query.trim().toLowerCase()
  const filtered = annotated.filter((item) => {
    if (streetFilter !== 'all' && item.street !== streetFilter) return false
    if (choiceFilter === 'strategic' && item.profile.questions === 0) return false
    if (choiceFilter === 'legacy' && item.profile.questions > 0) return false
    if (!normalizedQuery) return true
    const text = `${item.spot} ${item.street} ${item.result.config.positions.oop} ${item.result.config.positions.ip} ${item.result.nodes[item.result.rootNodeId]?.board.join(' ')}`.toLowerCase()
    return text.includes(normalizedQuery)
  })
  const groups = Array.from(filtered.reduce((map, item) => {
    const group = map.get(item.groupKey) ?? []
    group.push(item)
    map.set(item.groupKey, group)
    return map
  }, new Map<string, typeof annotated>()).entries())
  const toggleGroup = (key: string) => setExpandedGroups((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key])
  return (
    <>
      <AppHeader title="保存済み結果" back={() => useAppStore.getState().setScreen('home')} />
      <main className="page saved-page">
        <div className="page-intro"><Database size={25} /><div><strong>この端末に保存</strong><span>アカウント・クラウド同期なし</span></div></div>
        {loading ? <div className="loading-line">読み込み中…</div> : items.length === 0 ? <div className="empty-state standalone"><HardDrive size={42} /><strong>保存結果はまだありません</strong><p>完了した計算は自動でIndexedDBへ保存されます。</p><button className="primary-button" onClick={() => useAppStore.getState().setScreen('setup')}>最初の局面を計算</button></div> : <>
          <section className="saved-overview"><div><span>全結果</span><strong>{items.length}</strong></div><div><span>3択以上</span><strong>{strategicCount}</strong></div><div><span>旧2択中心</span><strong>{items.length - strategicCount}</strong></div></section>
          <div className="saved-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="ポジション・局面・カードを検索" aria-label="保存結果を検索" /></div>
          <div className="saved-filter-row">{(['all', 'flop', 'turn', 'river'] as const).map((street) => <button key={street} className={streetFilter === street ? 'active' : ''} onClick={() => setStreetFilter(street)}>{street === 'all' ? 'すべて' : street.toUpperCase()}</button>)}</div>
          <div className="saved-filter-row choice-filter"><button className={choiceFilter === 'all' ? 'active' : ''} onClick={() => setChoiceFilter('all')}>全ツリー</button><button className={choiceFilter === 'strategic' ? 'active' : ''} onClick={() => setChoiceFilter('strategic')}>3択以上</button><button className={choiceFilter === 'legacy' ? 'active' : ''} onClick={() => setChoiceFilter('legacy')}>旧2択</button></div>
          {filtered.length === 0 ? <div className="empty-state filtered-empty"><Search size={32} /><strong>該当する結果がありません</strong><p>検索語か絞り込みを変更してください。</p></div> : <div className="saved-groups">{groups.map(([key, group]) => {
            const expanded = expandedGroups.includes(key) || !!normalizedQuery
            const first = group[0]
            return <section key={key} className="saved-group">
              <button className="saved-group-head" onClick={() => toggleGroup(key)}><div><strong>{first.spot}</strong><span>{first.street.toUpperCase()} · {group.length}局面</span></div><div className="saved-group-preview">{first.result.nodes[first.result.rootNodeId]?.board.slice(0, 3).map((card) => <CardChip small card={card} key={card} />)}</div><ChevronRight className={expanded ? 'expanded' : ''} size={18} /></button>
              {expanded && <div className="saved-list">{group.map(({ result, profile, street }) => <article key={result.id}>
                <button className="saved-main" onClick={() => open(result)}><div className="saved-board">{result.nodes[result.rootNodeId]?.board.map((card) => <CardChip small card={card} key={card} />)}</div><div><div className="saved-tags"><em>{street.toUpperCase()}</em><em className={profile.questions > 0 ? 'strategic' : 'legacy'}>{profile.questions > 0 ? `最大${profile.maximumChoices}択` : '旧2択'}</em></div><strong>{new Date(result.createdAt).toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong><span>{presetMeta[result.config.preset].name} · {formatTime(result.metadata.durationMs)} · {result.metadata.iterations}反復</span><small>{profile.questions.toLocaleString()}問を通常トレーニングへ追加</small></div><ChevronRight size={18} /></button>
                <div className="saved-item-actions"><button onClick={() => recalculate(result)} aria-label="複数サイズで再計算"><RefreshCw size={16} /></button><button className="delete-button" onClick={() => remove(result.id)} aria-label="削除"><Trash2 size={16} /></button></div>
              </article>)}</div>}
            </section>
          })}</div>}
          <Notice><strong>再計算しても上書きしません</strong><p>↻ボタンは同じボードとレンジを複数サイズ・レイズ・All-in設定で開き、新しい結果として保存します。旧結果は必要なときだけ削除できます。</p></Notice>
        </>}
      </main>
    </>
  )
}

function BenchmarkScreen() {
  const [running, setRunning] = useState(false)
  const [benchmark, setBenchmark] = useState<DeviceBenchmark | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const run = async () => {
    setRunning(true)
    setMessage(null)
    try {
      const base = await runBrowserBenchmark()
      const idbStart = performance.now()
      await db.settings.put({ key: 'benchmark-probe', value: { time: Date.now(), payload: 'x'.repeat(4096) } })
      const indexedDbMs = performance.now() - idbStart
      const solveStart = performance.now()
      await solverClient.solve(createBenchmarkConfig(), () => undefined)
      const riverSolveMs = performance.now() - solveStart
      const completed: DeviceBenchmark = { ...base, indexedDbMs, riverSolveMs }
      await db.benchmarks.put(completed)
      setBenchmark(completed)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'ベンチマークに失敗しました')
    } finally {
      setRunning(false)
    }
  }
  return (
    <>
      <AppHeader title="端末性能テスト" back={() => useAppStore.getState().setScreen('home')} />
      <main className="page benchmark-page">
        <section className="benchmark-hero"><Gauge size={34} /><h1>このSafariで安全に<br />始められるか確認</h1><p>上限までメモリを確保するテストではありません。64MBまでの段階確保と小型Riverソルブを行います。</p><button className="primary-button" disabled={running} onClick={run}>{running ? <Activity className="spin" size={19} /> : <Play fill="currentColor" size={19} />}{running ? 'テスト実行中…' : benchmark ? 'もう一度テスト' : '性能テストを開始'}</button></section>
        {message && <Notice type="danger"><strong>テスト失敗</strong><p>{message}</p></Notice>}
        {benchmark && <>
          <section className="benchmark-score"><ShieldCheck size={27} /><div><span>推奨実行方式</span><strong>シングルスレッド</strong><small>最初はRiver Test / 省メモリ</small></div></section>
          <section className="section-card check-list">
            <div><span><BrainCircuit size={17} />WebAssembly</span><strong className={benchmark.wasm ? 'ok' : 'bad'}>{benchmark.wasm ? '利用可能' : '利用不可'}</strong></div>
            <div><span><Activity size={17} />Web Worker</span><strong className={benchmark.worker ? 'ok' : 'bad'}>{benchmark.worker ? '利用可能' : '利用不可'}</strong></div>
            <div><span><Layers3 size={17} />SharedArrayBuffer</span><strong>{benchmark.sharedArrayBuffer ? '利用可能' : '未使用'}</strong></div>
            <div><span><BatteryCharging size={17} />Wake Lock</span><strong>{benchmark.wakeLock ? '対応' : '未対応'}</strong></div>
            <div><span><MemoryStick size={17} />一時配列確保</span><strong>{benchmark.typedArrayMb}MB 確認</strong></div>
            <div><span><Database size={17} />IndexedDB書込</span><strong>{benchmark.indexedDbMs?.toFixed(1)}ms</strong></div>
            <div><span><Zap size={17} />小型Riverソルブ</span><strong>{formatTime(benchmark.riverSolveMs)}</strong></div>
          </section>
          <Notice><strong>RAM容量を示す値ではありません</strong><p>一時配列テストは安全側で打ち切っています。実際のツリーは設定画面のソルバー見積もりで判定します。</p></Notice>
        </>}
      </main>
    </>
  )
}

function LicenseScreen() {
  return (
    <>
      <AppHeader title="ライセンスと制限" back={() => useAppStore.getState().setScreen('home')} />
      <main className="page legal-page">
        <section className="section-card"><div className="section-title"><BookOpen size={19} /><div><strong>オープンソース</strong><span>AGPL-3.0-or-later</span></div></div><p>本アプリは b-inary/wasm-postflop と b-inary/postflop-solver を利用しています。原著作権表示、対応ソース、ビルド由来、改変内容は公開リポジトリ内に保持します。</p><a href="https://github.com/b-inary/wasm-postflop" target="_blank" rel="noreferrer">wasm-postflop 原本</a><a href="https://github.com/b-inary/postflop-solver" target="_blank" rel="noreferrer">postflop-solver 原本</a></section>
        <section className="section-card"><div className="section-title"><AlertTriangle size={19} /><div><strong>重要な制限</strong><span>実験版</span></div></div><ul><li>実ソルバーはヘッズアップ・ポストフロップのみ</li><li>ポストフロップ計算はレーキなし</li><li>プリフロップ問題は100bb・6-max・レーキなしの学習用ベースラインで、ソルバー出力ではない</li><li>iOSではシングルスレッドのみ</li><li>バックグラウンド計算なし</li><li>Flop Standardは実験的</li><li>説明はAIではなくルールベース</li></ul></section>
        <section className="section-card"><div className="section-title"><WifiOff size={19} /><div><strong>プライバシー</strong><span>端末内完結</span></div></div><p>局面・レンジ・結果はサーバーへ送信せず、この端末のIndexedDBへ保存されます。アカウントや端末間同期はありません。</p></section>
      </main>
    </>
  )
}

export default function App() {
  const { screen, setScreen, config, estimate, setEstimate, setProgress, setResult, setError, error } = useAppStore()
  const wakeLockRef = useRef<{ release: () => Promise<void> } | null>(null)

  useEffect(() => () => solverClient.terminate(), [])

  const go = (next: Screen) => {
    setError(null)
    setScreen(next)
  }

  const start = async () => {
    setError(null)
    try {
      if (!estimate) setEstimate(await solverClient.estimate(config))
      if (useAppStore.getState().estimate?.rating === 'not-recommended') return
      setScreen('solving')
      setProgress(null)
      try {
        if ('wakeLock' in navigator) wakeLockRef.current = await (navigator as Navigator & { wakeLock: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> } }).wakeLock.request('screen')
      } catch { /* Wake Lock is best-effort. */ }
      const result = await solverClient.solve(config, setProgress, setEstimate)
      await saveSolverResult(result)
      setResult(result)
      setScreen('result')
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') return
      const solverError = (caught as { solverError?: SolverError }).solverError
      setError(solverError ?? { code: 'SOLVE_FAILED', message: caught instanceof Error ? caught.message : '計算に失敗しました' })
      setScreen('setup')
    } finally {
      await wakeLockRef.current?.release().catch(() => undefined)
      wakeLockRef.current = null
    }
  }

  const stop = () => {
    solverClient.cancel()
    setProgress(null)
    setScreen('setup')
  }

  return (
    <div className="app-shell">
      {error && screen !== 'solving' && <div className="global-error"><AlertTriangle size={18} /><div><strong>{error.message}</strong>{error.detail && <small>{error.detail}</small>}</div><button onClick={() => setError(null)}><X size={17} /></button></div>}
      {screen === 'home' && <HomeScreen go={go} />}
      {screen === 'setup' && <SetupScreen onStart={start} />}
      {screen === 'batch' && <BatchScreen />}
      {screen === 'solving' && <SolvingScreen stop={stop} />}
      {screen === 'result' && <ResultScreen />}
      {screen === 'trainer' && <TrainerScreen />}
      {screen === 'saved' && <SavedScreen />}
      {screen === 'benchmark' && <BenchmarkScreen />}
      {screen === 'license' && <LicenseScreen />}
      <BottomNav screen={screen} go={go} />
    </div>
  )
}
